import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Columns3,
  ExternalLink,
  GitCommitHorizontal,
  GripVertical,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Rocket,
  Search,
  ShieldCheck,
  TestTube2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { sanitizeInput } from '@/components/security/input-sanitizer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { fetchDevelopmentTasks } from '@/domains/development-tasks/services/development-tasks.service';
import { useAuth } from '@/providers/auth-provider';

type DevelopmentTaskStatus =
  | 'submitted'
  | 'reviewing'
  | 'blocked'
  | 'in_development'
  | 'dev_testing'
  | 'ready_for_live'
  | 'deploying_live'
  | 'live_testing'
  | 'awaiting_team_test'
  | 'done'
  | 'failed'
  | 'cancelled';

interface DevelopmentTask {
  id: string;
  title: string;
  description: string;
  status: DevelopmentTaskStatus;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  source: 'admin' | 'slack' | 'codex' | 'automation';
  organization_id: string | null;
  processing_mode: 'automatic' | 'manual';
  source_channel_id: string | null;
  source_thread_ts: string | null;
  source_message_ts: string | null;
  requested_by: string | null;
  assigned_to: string;
  repository: string;
  current_environment: 'dev' | 'staging' | 'live';
  dev_commit_sha: string | null;
  dev_test_evidence: string | null;
  live_commit_sha: string | null;
  live_deployment_url: string | null;
  live_test_evidence: string | null;
  jira_epic_key: string | null;
  jira_issue_key: string | null;
  jira_issue_url: string | null;
  jira_sync_status: 'pending' | 'syncing' | 'synced' | 'failed';
  jira_sync_error: string | null;
  live_approved_at: string | null;
  team_test_requested_at: string | null;
  team_notification_sent_at: string | null;
  team_test_evidence: string | null;
  team_tested_at: string | null;
  team_review_round: number;
  team_review_next_check_at: string | null;
  team_review_last_checked_at: string | null;
  team_review_last_comment_at: string | null;
  team_review_last_comment_id: string | null;
  team_review_last_comment: string | null;
  team_review_comment_count: number;
  current_blocker: string | null;
  next_automation_at: string;
  last_automation_check_at: string | null;
  created_at: string;
  updated_at: string;
  status_changed_at: string;
}

interface TransitionEvidence {
  devCommitSha: string;
  devTestEvidence: string;
  liveCommitSha: string;
  liveDeploymentUrl: string;
  liveTestEvidence: string;
}

interface NewTaskForm {
  title: string;
  description: string;
  acceptanceCriteria: string;
  priority: DevelopmentTask['priority'];
  processingMode: DevelopmentTask['processing_mode'];
}

const STATUS_ORDER: DevelopmentTaskStatus[] = [
  'submitted',
  'reviewing',
  'blocked',
  'in_development',
  'dev_testing',
  'ready_for_live',
  'deploying_live',
  'live_testing',
  'awaiting_team_test',
  'done',
  'failed',
  'cancelled',
];

const STATUS_CONFIG: Record<DevelopmentTaskStatus, { label: string; className: string }> = {
  submitted: { label: 'Submitted', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  reviewing: { label: 'Reviewing', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  blocked: { label: 'Blocked', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  in_development: { label: 'In development', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  dev_testing: { label: 'Dev testing', className: 'bg-violet-100 text-violet-700 border-violet-200' },
  ready_for_live: { label: 'Ready for live', className: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  deploying_live: { label: 'Deploying live', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  live_testing: { label: 'Live testing', className: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200' },
  awaiting_team_test: { label: 'Awaiting team comments', className: 'bg-teal-100 text-teal-800 border-teal-200' },
  done: { label: 'Done', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-600 border-gray-200' },
};

interface BoardColumn {
  id: string;
  title: string;
  statuses: DevelopmentTaskStatus[];
  dropStatus?: DevelopmentTaskStatus;
  accentClassName: string;
}

const BOARD_COLUMNS: BoardColumn[] = [
  {
    id: 'submitted',
    title: 'Submitted',
    statuses: ['submitted'],
    dropStatus: 'submitted',
    accentClassName: 'border-t-slate-400',
  },
  {
    id: 'reviewing',
    title: 'Review',
    statuses: ['reviewing'],
    dropStatus: 'reviewing',
    accentClassName: 'border-t-blue-500',
  },
  {
    id: 'in-development',
    title: 'Development',
    statuses: ['in_development'],
    dropStatus: 'in_development',
    accentClassName: 'border-t-indigo-500',
  },
  {
    id: 'dev-testing',
    title: 'Dev testing',
    statuses: ['dev_testing'],
    dropStatus: 'dev_testing',
    accentClassName: 'border-t-violet-500',
  },
  {
    id: 'blocked',
    title: 'Blocked',
    statuses: ['blocked'],
    dropStatus: 'blocked',
    accentClassName: 'border-t-amber-500',
  },
  {
    id: 'ready-live',
    title: 'Ready for live',
    statuses: ['ready_for_live'],
    dropStatus: 'ready_for_live',
    accentClassName: 'border-t-cyan-500',
  },
  {
    id: 'deploying-live',
    title: 'Live deployment',
    statuses: ['deploying_live'],
    dropStatus: 'deploying_live',
    accentClassName: 'border-t-orange-500',
  },
  {
    id: 'live-testing',
    title: 'Live testing',
    statuses: ['live_testing'],
    dropStatus: 'live_testing',
    accentClassName: 'border-t-fuchsia-500',
  },
  {
    id: 'team-review',
    title: 'Team comments',
    statuses: ['awaiting_team_test'],
    dropStatus: 'awaiting_team_test',
    accentClassName: 'border-t-teal-500',
  },
  {
    id: 'done',
    title: 'Done',
    statuses: ['done'],
    accentClassName: 'border-t-emerald-500',
  },
  {
    id: 'closed',
    title: 'Closed',
    statuses: ['failed', 'cancelled'],
    accentClassName: 'border-t-gray-400',
  },
];

function compactEvidence(value: string | null, empty: string) {
  if (!value) return <span className="text-gray-400">{empty}</span>;
  return <span className="line-clamp-2" title={value}>{value}</span>;
}

function safeHttpsUrl(value: string | null) {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

const ALLOWED_TRANSITIONS: Record<DevelopmentTaskStatus, DevelopmentTaskStatus[]> = {
  submitted: ['reviewing', 'cancelled'],
  reviewing: ['blocked', 'in_development', 'failed', 'cancelled'],
  blocked: ['reviewing', 'in_development', 'failed', 'cancelled'],
  in_development: ['blocked', 'dev_testing', 'failed', 'cancelled'],
  dev_testing: ['blocked', 'in_development', 'ready_for_live', 'failed', 'cancelled'],
  ready_for_live: ['in_development', 'deploying_live', 'failed', 'cancelled'],
  deploying_live: ['blocked', 'live_testing', 'failed'],
  live_testing: ['blocked', 'deploying_live', 'awaiting_team_test', 'failed'],
  awaiting_team_test: ['in_development', 'live_testing', 'failed'],
  failed: ['reviewing', 'cancelled'],
  cancelled: ['reviewing'],
  done: [],
};

async function taskApi<T>(path: string, accessToken: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok || !result.ok) {
    throw new Error(result.error || `Task request failed with HTTP ${response.status}`);
  }
  return result as T;
}

export default function DevelopmentTasksPage() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | DevelopmentTaskStatus>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [submissionKey, setSubmissionKey] = useState(() => crypto.randomUUID());
  const [newTask, setNewTask] = useState<NewTaskForm>({
    title: '',
    description: '',
    acceptanceCriteria: '',
    priority: 'normal',
    processingMode: 'automatic',
  });
  const [transition, setTransition] = useState<{
    task: DevelopmentTask;
    nextStatus: DevelopmentTaskStatus;
  } | null>(null);
  const [transitionNote, setTransitionNote] = useState('');
  const [transitionEvidence, setTransitionEvidence] = useState<TransitionEvidence>({
    devCommitSha: '',
    devTestEvidence: '',
    liveCommitSha: '',
    liveDeploymentUrl: '',
    liveTestEvidence: '',
  });
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);

  const tasksQuery = useQuery({
    queryKey: ['development-tasks'],
    queryFn: () => fetchDevelopmentTasks<DevelopmentTask>(),
    refetchInterval: 15_000,
  });

  const transitionMutation = useMutation({
    mutationFn: async ({ task, nextStatus, note, evidence }: {
      task: DevelopmentTask;
      nextStatus: DevelopmentTaskStatus;
      note: string;
      evidence: TransitionEvidence;
    }) => {
      if (!session?.access_token) throw new Error('Your session has expired');
      return taskApi<{ task: DevelopmentTask; warnings: string[] }>('/api/tasks/status', session.access_token, {
        taskId: task.id,
        status: nextStatus,
        note: sanitizeInput(note),
        evidence: {
          devCommitSha: sanitizeInput(evidence.devCommitSha),
          devTestEvidence: sanitizeInput(evidence.devTestEvidence),
          liveCommitSha: sanitizeInput(evidence.liveCommitSha),
          liveDeploymentUrl: sanitizeInput(evidence.liveDeploymentUrl),
          liveTestEvidence: sanitizeInput(evidence.liveTestEvidence),
        },
      });
    },
    onSuccess: async result => {
      await queryClient.invalidateQueries({ queryKey: ['development-tasks'] });
      toast.success('Task status updated');
      result.warnings?.forEach(warning => toast.warning(warning));
      setTransition(null);
      setTransitionNote('');
      setTransitionEvidence({
        devCommitSha: '',
        devTestEvidence: '',
        liveCommitSha: '',
        liveDeploymentUrl: '',
        liveTestEvidence: '',
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const createMutation = useMutation({
    mutationFn: async (form: NewTaskForm) => {
      if (!session?.access_token) throw new Error('Your session has expired');
      return taskApi<{
        task: DevelopmentTask;
        warning?: string;
      }>('/api/tasks/create', session.access_token, {
        title: sanitizeInput(form.title),
        description: sanitizeInput(form.description),
        acceptanceCriteria: sanitizeInput(form.acceptanceCriteria),
        priority: form.priority,
        processingMode: form.processingMode,
        submissionKey,
      });
    },
    onSuccess: async result => {
      await queryClient.invalidateQueries({ queryKey: ['development-tasks'] });
      toast.success(result.task.jira_issue_key
        ? `Task created with Jira ${result.task.jira_issue_key}`
        : 'Task created');
      if (result.warning) toast.warning(result.warning);
      setCreateDialogOpen(false);
      setSubmissionKey(crypto.randomUUID());
      setNewTask({
        title: '',
        description: '',
        acceptanceCriteria: '',
        priority: 'normal',
        processingMode: 'automatic',
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const jiraSyncMutation = useMutation({
    mutationFn: async (taskId: string) => {
      if (!session?.access_token) throw new Error('Your session has expired');
      return taskApi<{ task: DevelopmentTask }>('/api/tasks/sync-jira', session.access_token, { taskId });
    },
    onSuccess: async result => {
      await queryClient.invalidateQueries({ queryKey: ['development-tasks'] });
      toast.success(`Jira ${result.task.jira_issue_key} synchronized`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const tasks = useMemo(() => tasksQuery.data ?? [], [tasksQuery.data]);
  const filteredTasks = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return tasks.filter(task => {
      if (statusFilter !== 'all' && task.status !== statusFilter) return false;
      if (!normalizedSearch) return true;
      return [
        task.title,
        task.description,
        task.requested_by,
        task.dev_commit_sha,
        task.live_commit_sha,
        task.jira_issue_key,
      ]
        .some(value => value?.toLowerCase().includes(normalizedSearch));
    });
  }, [search, statusFilter, tasks]);

  const metrics = useMemo(() => ({
    submitted: tasks.filter(task => task.status === 'submitted').length,
    active: tasks.filter(task => ['reviewing', 'in_development', 'dev_testing', 'deploying_live', 'live_testing'].includes(task.status)).length,
    blocked: tasks.filter(task => task.status === 'blocked').length,
    ready: tasks.filter(task => task.status === 'ready_for_live').length,
    teamTest: tasks.filter(task => task.status === 'awaiting_team_test').length,
    done: tasks.filter(task => task.status === 'done').length,
  }), [tasks]);

  const automationHealth = useMemo(() => {
    const automaticQueue = tasks.filter(task => (
      task.processing_mode === 'automatic' && task.status === 'submitted'
    ));
    const latestPickup = tasks
      .map(task => task.last_automation_check_at)
      .filter((value): value is string => Boolean(value))
      .sort()
      .at(-1) ?? null;
    const now = Date.now();
    const overdueTasks = automaticQueue.filter(task => (
      new Date(task.next_automation_at).getTime() <= now
    ));
    const oldestDueAt = overdueTasks
      .map(task => new Date(task.next_automation_at).getTime())
      .sort((a, b) => a - b)[0];
    const pickupIsStale = latestPickup
      ? now - new Date(latestPickup).getTime() > 75 * 60 * 1_000
      : true;
    const queueIsOverdue = oldestDueAt !== undefined && now - oldestDueAt > 75 * 60 * 1_000;

    if (overdueTasks.length > 0 && (pickupIsStale || queueIsOverdue)) {
      return {
        tone: 'warning' as const,
        label: 'Hourly pickup not confirmed',
        detail: `${overdueTasks.length} automatic ${overdueTasks.length === 1 ? 'request is' : 'requests are'} waiting. No recent Codex pickup is recorded.`,
        latestPickup,
      };
    }
    if (latestPickup) {
      return {
        tone: 'healthy' as const,
        label: 'Codex pickup recorded',
        detail: `Last automatic pickup ${formatDistanceToNow(new Date(latestPickup), { addSuffix: true })}.`,
        latestPickup,
      };
    }
    return {
      tone: 'neutral' as const,
      label: automaticQueue.length > 0 ? 'Waiting for first Codex pickup' : 'No automatic work queued',
      detail: automaticQueue.length > 0
        ? 'The requests are eligible, but no hourly worker run has been recorded yet.'
        : 'A runtime check will appear here after Codex claims an automatic request.',
      latestPickup: null,
    };
  }, [tasks]);

  const transitionEvidenceComplete = !transition || (
    (!['ready_for_live', 'deploying_live', 'live_testing', 'awaiting_team_test', 'done'].includes(transition.nextStatus)
      || Boolean(transitionEvidence.devCommitSha.trim() && transitionEvidence.devTestEvidence.trim()))
    && (!['live_testing', 'awaiting_team_test', 'done'].includes(transition.nextStatus)
      || Boolean(transitionEvidence.liveCommitSha.trim() && transitionEvidence.liveDeploymentUrl.trim()))
    && (!['awaiting_team_test', 'done'].includes(transition.nextStatus)
      || Boolean(transitionEvidence.liveTestEvidence.trim()))
    && (transition.nextStatus !== 'blocked' || Boolean(transitionNote.trim()))
  );

  const beginTransition = (task: DevelopmentTask, nextStatus: DevelopmentTaskStatus) => {
    setTransition({ task, nextStatus });
    setTransitionNote('');
    setTransitionEvidence({
      devCommitSha: task.dev_commit_sha ?? '',
      devTestEvidence: task.dev_test_evidence ?? '',
      liveCommitSha: task.live_commit_sha ?? '',
      liveDeploymentUrl: task.live_deployment_url ?? '',
      liveTestEvidence: task.live_test_evidence ?? '',
    });
  };

  const moveDraggedTask = (column: BoardColumn) => {
    if (!draggedTaskId || !column.dropStatus) return;
    const task = filteredTasks.find(item => item.id === draggedTaskId);
    if (!task || task.status === column.dropStatus) return;
    if (!ALLOWED_TRANSITIONS[task.status].includes(column.dropStatus)) {
      toast.error(
        `Move through the allowed workflow first. ${STATUS_CONFIG[task.status].label} cannot move directly to ${STATUS_CONFIG[column.dropStatus].label}.`,
      );
      return;
    }
    beginTransition(task, column.dropStatus);
  };

  if (tasksQuery.isLoading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-6 p-6 lg:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-950">Business Requests</h1>
          <p className="mt-1 text-sm text-gray-600">
            Submit what you need and follow it from review through testing and delivery.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => tasksQuery.refetch()} disabled={tasksQuery.isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${tasksQuery.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => {
            createMutation.reset();
            setSubmissionKey(crypto.randomUUID());
            setCreateDialogOpen(true);
          }}>
            <Plus className="mr-2 h-4 w-4" />
            New request
          </Button>
        </div>
      </div>

      {tasksQuery.isError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex items-center gap-3 p-4 text-sm text-red-700">
            <AlertTriangle className="h-5 w-5" />
            {(tasksQuery.error as Error).message}
          </CardContent>
        </Card>
      )}

      <Card className={automationHealth.tone === 'warning'
        ? 'border-amber-300 bg-amber-50'
        : automationHealth.tone === 'healthy'
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-slate-200 bg-slate-50'}
      >
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={automationHealth.tone === 'warning'
              ? 'rounded-full bg-amber-100 p-2 text-amber-700'
              : automationHealth.tone === 'healthy'
                ? 'rounded-full bg-emerald-100 p-2 text-emerald-700'
                : 'rounded-full bg-slate-200 p-2 text-slate-600'}
            >
              {automationHealth.tone === 'warning'
                ? <AlertTriangle className="h-5 w-5" />
                : <Bot className="h-5 w-5" />}
            </div>
            <div>
              <p className="font-semibold text-gray-950">{automationHealth.label}</p>
              <p className="mt-0.5 text-sm text-gray-600">{automationHealth.detail}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            “Automatic” means eligible for pickup; this banner shows whether a pickup was actually recorded.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {[
          { label: 'Submitted', value: metrics.submitted, icon: CircleDashed, color: 'text-slate-600' },
          { label: 'Active', value: metrics.active, icon: Clock3, color: 'text-blue-600' },
          { label: 'Blocked', value: metrics.blocked, icon: AlertTriangle, color: 'text-amber-600' },
          { label: 'Ready for live', value: metrics.ready, icon: Rocket, color: 'text-cyan-600' },
          { label: 'Team comments', value: metrics.teamTest, icon: Users, color: 'text-teal-600' },
          { label: 'Done', value: metrics.done, icon: CheckCircle2, color: 'text-emerald-600' },
        ].map(metric => (
          <Card key={metric.label}>
            <CardContent className="flex items-center justify-between p-4 sm:p-5">
              <div>
                <p className="text-sm text-gray-500">{metric.label}</p>
                <p className="mt-1 text-2xl font-bold text-gray-950">{metric.value}</p>
              </div>
              <metric.icon className={`h-6 w-6 ${metric.color}`} />
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="min-w-0 overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Columns3 className="h-5 w-5 text-blue-600" />
                Delivery board
              </CardTitle>
              <p className="mt-1 text-xs text-gray-500">
                Drag a card to an allowed next column, or use its Move menu. Approval and evidence gates still apply.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative sm:w-80">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Search tasks or commit SHA"
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={value => setStatusFilter(value as 'all' | DevelopmentTaskStatus)}>
                <SelectTrigger className="sm:w-48">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_ORDER.map(status => (
                    <SelectItem key={status} value={status}>{STATUS_CONFIG[status].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="min-w-0">
          {filteredTasks.length === 0 ? (
            <div className="rounded-lg border border-dashed px-6 py-14 text-center">
              <ShieldCheck className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-3 font-medium text-gray-700">No development tasks yet</p>
              <p className="mt-1 text-sm text-gray-500">Create one here or submit a request through TimeFlow Slack.</p>
            </div>
          ) : (
            <div className="w-full max-w-full overflow-x-auto overscroll-x-contain pb-3">
              <div className="flex w-max gap-4">
                {BOARD_COLUMNS.map(column => {
                  const columnTasks = filteredTasks.filter(task => column.statuses.includes(task.status));
                  const draggedTask = draggedTaskId
                    ? filteredTasks.find(task => task.id === draggedTaskId)
                    : null;
                  const acceptsDraggedTask = Boolean(
                    draggedTask
                    && column.dropStatus
                    && ALLOWED_TRANSITIONS[draggedTask.status].includes(column.dropStatus),
                  );

                  return (
                    <section
                      key={column.id}
                      aria-label={`${column.title} tasks`}
                      onDragOver={event => {
                        if (draggedTaskId) event.preventDefault();
                      }}
                      onDrop={event => {
                        event.preventDefault();
                        moveDraggedTask(column);
                        setDraggedTaskId(null);
                      }}
                      className={`flex w-80 shrink-0 flex-col rounded-xl border border-t-4 bg-gray-50/80 ${column.accentClassName} ${
                        draggedTaskId && acceptsDraggedTask ? 'ring-2 ring-blue-400 ring-offset-2' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between border-b border-gray-200 px-3 py-3">
                        <h2 className="text-sm font-semibold text-gray-900">{column.title}</h2>
                        <Badge variant="secondary" className="min-w-7 justify-center">{columnTasks.length}</Badge>
                      </div>
                      <div className="min-h-40 flex-1 space-y-3 p-3">
                        {columnTasks.length === 0 && (
                          <div className="flex min-h-28 items-center justify-center rounded-lg border border-dashed border-gray-300 px-4 text-center text-xs text-gray-400">
                            {draggedTaskId && acceptsDraggedTask ? 'Drop card here' : 'No requests'}
                          </div>
                        )}
                        {columnTasks.map(task => {
                          const status = STATUS_CONFIG[task.status];
                          const taskDueAt = new Date(task.next_automation_at);
                          const automaticTaskWaiting = task.processing_mode === 'automatic' && task.status === 'submitted';
                          return (
                            <article
                              key={task.id}
                              draggable={ALLOWED_TRANSITIONS[task.status].length > 0}
                              onDragStart={event => {
                                event.dataTransfer.effectAllowed = 'move';
                                event.dataTransfer.setData('text/plain', task.id);
                                setDraggedTaskId(task.id);
                              }}
                              onDragEnd={() => setDraggedTaskId(null)}
                              className={`rounded-lg border bg-white p-3 shadow-sm transition ${
                                draggedTaskId === task.id ? 'opacity-50' : 'hover:border-blue-200 hover:shadow'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <Badge variant="outline" className={status.className}>{status.label}</Badge>
                                  <Badge variant="outline" className="capitalize">{task.priority}</Badge>
                                </div>
                                {ALLOWED_TRANSITIONS[task.status].length > 0 && (
                                  <GripVertical
                                    className="mt-1 h-4 w-4 shrink-0 cursor-grab text-gray-400"
                                    aria-label="Drag task"
                                  />
                                )}
                              </div>

                              <h3 className="mt-3 line-clamp-2 text-sm font-semibold leading-5 text-gray-950">
                                {task.title}
                              </h3>
                              <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-gray-500">
                                {task.description}
                              </p>

                              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                                {task.jira_issue_url ? (
                                  <a
                                    href={task.jira_issue_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
                                  >
                                    <Link2 className="h-3.5 w-3.5" />
                                    {task.jira_issue_key}
                                  </a>
                                ) : (
                                  <span className={task.jira_sync_status === 'failed' ? 'text-red-600' : 'text-amber-700'}>
                                    Jira {task.jira_sync_status}
                                  </span>
                                )}
                                <span className="text-gray-400">#{task.id.slice(0, 8)}</span>
                              </div>

                              {automaticTaskWaiting && (
                                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                                  <div className="flex items-center gap-1.5 font-medium">
                                    <Bot className="h-3.5 w-3.5" />
                                    Waiting for Codex pickup
                                  </div>
                                  <div className="mt-1 text-amber-700">
                                    Eligible {formatDistanceToNow(taskDueAt, { addSuffix: true })}
                                  </div>
                                </div>
                              )}

                              {task.current_blocker && (
                                <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                  <span className="line-clamp-3">{task.current_blocker}</span>
                                </div>
                              )}

                              <details className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-600">
                                <summary className="cursor-pointer font-medium text-gray-700">Delivery details</summary>
                                <div className="mt-2 space-y-2">
                                  <div>
                                    <div className="flex items-center gap-1 font-medium text-gray-700">
                                      <GitCommitHorizontal className="h-3.5 w-3.5" /> Dev
                                    </div>
                                    <div>{task.dev_commit_sha ? <code>{task.dev_commit_sha}</code> : 'No dev SHA'}</div>
                                    <div>{compactEvidence(task.dev_test_evidence, 'Not dev-tested')}</div>
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-1 font-medium text-gray-700">
                                      <TestTube2 className="h-3.5 w-3.5" /> Live
                                    </div>
                                    <div>{task.live_commit_sha ? <code>{task.live_commit_sha}</code> : 'No live SHA'}</div>
                                    <div>{compactEvidence(task.live_test_evidence, 'Not live-tested')}</div>
                                    {safeHttpsUrl(task.live_deployment_url) && (
                                      <a
                                        href={safeHttpsUrl(task.live_deployment_url) ?? undefined}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:underline"
                                      >
                                        Open deployment <ExternalLink className="h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-1 font-medium text-teal-800">
                                      <Users className="h-3.5 w-3.5" /> Team review
                                    </div>
                                    <div>
                                      {compactEvidence(task.team_test_evidence, task.team_test_requested_at
                                        ? `Round ${task.team_review_round}: waiting for Jira comment check`
                                        : 'Not requested yet')}
                                    </div>
                                    {task.team_review_last_comment && (
                                      <div className="mt-1 rounded bg-teal-50 p-2 text-teal-900">
                                        Latest: {task.team_review_last_comment}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </details>

                              <div className="mt-3 space-y-2">
                                <Select
                                  value={task.status}
                                  onValueChange={value => {
                                    const nextStatus = value as DevelopmentTaskStatus;
                                    if (nextStatus !== task.status) beginTransition(task, nextStatus);
                                  }}
                                >
                                  <SelectTrigger className="h-9 text-xs" aria-label={`Move ${task.title}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[task.status, ...ALLOWED_TRANSITIONS[task.status]].map(option => (
                                      <SelectItem key={option} value={option}>{STATUS_CONFIG[option].label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>

                                {task.status === 'submitted' && (
                                  <Button
                                    className="h-9 w-full text-xs"
                                    variant={task.processing_mode === 'manual' ? 'default' : 'outline'}
                                    onClick={() => beginTransition(task, 'reviewing')}
                                  >
                                    {task.processing_mode === 'manual' ? 'Start now' : 'Claim now'}
                                  </Button>
                                )}
                                {task.status === 'ready_for_live' && (
                                  <Button
                                    className="h-9 w-full text-xs"
                                    onClick={() => beginTransition(task, 'deploying_live')}
                                  >
                                    <Rocket className="mr-2 h-3.5 w-3.5" />
                                    Approve live
                                  </Button>
                                )}
                                {task.jira_sync_status === 'failed' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 w-full text-xs"
                                    disabled={jiraSyncMutation.isPending}
                                    onClick={() => jiraSyncMutation.mutate(task.id)}
                                  >
                                    Retry Jira
                                  </Button>
                                )}
                              </div>

                              <div className="mt-3 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
                                <div>
                                  {task.processing_mode === 'automatic' ? 'Automatic' : 'Manual'}
                                  {' · '}Assigned to {task.assigned_to}
                                </div>
                                <div className="mt-1">
                                  Updated {formatDistanceToNow(new Date(task.updated_at), { addSuffix: true })}
                                </div>
                                {task.requested_by && <div className="mt-1">By {task.requested_by}</div>}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createDialogOpen}
        onOpenChange={open => {
          setCreateDialogOpen(open);
          if (!open) createMutation.reset();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Request work</DialogTitle>
            <DialogDescription>
              Describe what you need in plain language. TimeFlow will create the delivery ticket
              and handle the technical workflow.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="task-title">What do you need?</Label>
              <Textarea
                id="task-title"
                value={newTask.title}
                onChange={event => setNewTask(current => ({ ...current, title: event.target.value }))}
                placeholder="Example: Show me the users who joined last week."
                rows={4}
                maxLength={200}
                autoFocus
              />
              <p className="text-xs text-gray-500">
                One clear sentence is enough. The delivery team can add technical details later.
              </p>
            </div>
            {createMutation.isError && (
              <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{(createMutation.error as Error).message}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              disabled={!newTask.title.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate(newTask)}
            >
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(transition)} onOpenChange={open => !open && setTransition(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {transition?.nextStatus === 'deploying_live' ? 'Approve live deployment' : 'Change task status'}
            </DialogTitle>
          </DialogHeader>
          {transition && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Move <span className="font-medium text-gray-900">{transition.task.title}</span> from{' '}
                <span className="font-medium">{STATUS_CONFIG[transition.task.status].label}</span> to{' '}
                <span className="font-medium">{STATUS_CONFIG[transition.nextStatus].label}</span>.
              </p>
              {transition.nextStatus === 'deploying_live' && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
                  This is the production authorization for this task. Confirm the exact dev SHA and
                  dev-test evidence before approving.
                </div>
              )}
              {['ready_for_live', 'deploying_live', 'live_testing', 'awaiting_team_test', 'done'].includes(transition.nextStatus) && (
                <div className="grid gap-3">
                  <Input
                    value={transitionEvidence.devCommitSha}
                    onChange={event => setTransitionEvidence(current => ({
                      ...current,
                      devCommitSha: event.target.value,
                    }))}
                    placeholder="Dev commit SHA"
                  />
                  <Textarea
                    value={transitionEvidence.devTestEvidence}
                    onChange={event => setTransitionEvidence(current => ({
                      ...current,
                      devTestEvidence: event.target.value,
                    }))}
                    placeholder="Dev test evidence"
                    rows={3}
                  />
                </div>
              )}
              {['live_testing', 'awaiting_team_test', 'done'].includes(transition.nextStatus) && (
                <div className="grid gap-3">
                  <Input
                    value={transitionEvidence.liveCommitSha}
                    onChange={event => setTransitionEvidence(current => ({
                      ...current,
                      liveCommitSha: event.target.value,
                    }))}
                    placeholder="Live commit SHA"
                  />
                  <Input
                    value={transitionEvidence.liveDeploymentUrl}
                    onChange={event => setTransitionEvidence(current => ({
                      ...current,
                      liveDeploymentUrl: event.target.value,
                    }))}
                    placeholder="Live deployment URL"
                  />
                </div>
              )}
              {['awaiting_team_test', 'done'].includes(transition.nextStatus) && (
                <Textarea
                  value={transitionEvidence.liveTestEvidence}
                  onChange={event => setTransitionEvidence(current => ({
                    ...current,
                    liveTestEvidence: event.target.value,
                  }))}
                  placeholder="Live test evidence"
                  rows={3}
                />
              )}
              <Textarea
                value={transitionNote}
                onChange={event => setTransitionNote(event.target.value)}
                placeholder="Status note or blocker details"
                rows={4}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransition(null)}>Cancel</Button>
            <Button
              disabled={!transition || !transitionEvidenceComplete || transitionMutation.isPending}
              onClick={() => transition && transitionMutation.mutate({
                task: transition.task,
                nextStatus: transition.nextStatus,
                note: transitionNote,
                evidence: transitionEvidence,
              })}
            >
              {transitionMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {transition?.nextStatus === 'deploying_live' ? 'Approve and continue' : 'Update status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
